import { DateTime } from 'luxon';
import { useSession } from '~/stores/session';
import { UserPreferenceKeys } from '~/composables/useCurrentUser';
import Settings from '../pages/course/[courseid]/settings.vue';

// TODO: refactor ALL methods to use JS Date in - JS Date out (no more date strings as return values etc.)
// This will give greater consistency, will be easier to test and is less error prone

export interface TimetableWeekDay {
  date: Date;
  machineDate: string;
  vacation?: string;
}

export function getFirstAndLastWorkDays(date: Date) {
  const currentDate = date;
  const firstday = new Date(
    currentDate.setDate(currentDate.getDate() - currentDate.getDay() + 1),
  );
  const lastday = new Date(
    currentDate.setDate(currentDate.getDate() - currentDate.getDay() + 5),
  );

  return {
    firstday,
    lastday,
  };
}

export function formatDayShort(date: Date) {
  return DateTime.fromJSDate(date).toLocaleString({
    weekday: 'short',
    month: 'numeric',
    day: 'numeric',
  });
}

export function formatDayLong(date: Date) {
  return DateTime.fromJSDate(date).toLocaleString({
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
}

export function formatDayLongWithYear(date: Date) {
  return DateTime.fromJSDate(date).toLocaleString({
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function formatDayShortWithYear(date: Date) {
  return DateTime.fromJSDate(date).toLocaleString({
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
  });
}

export function getFirstAndLastWorkDayWeekFromWeekAndYearNumber(
  weekNumber: number,
  yearNumber: number,
): { firstWorkDay: Date; lastWorkDay: Date } {
  const dt = DateTime.fromObject({
    weekYear: yearNumber,
    weekNumber: weekNumber,
  });

  const firstWorkDay = dt.startOf('week').toJSDate();
  const lastWorkDay = dt.endOf('week').minus({ days: 2 }).toJSDate();

  return {
    firstWorkDay,
    lastWorkDay,
  };
}

export function getCalendarYearNumber(date: Date): number {
  return date.getFullYear();
}

export function getCalendarWeek(date: Date): number {
  return DateTime.fromJSDate(date).weekNumber;
}

export function getFirstDayOfWeek(date: Date) {
  return DateTime.fromJSDate(date).startOf('week').toJSDate();
}

export function getLastDayOfWeek(date: Date) {
  return DateTime.fromJSDate(date).endOf('week').toJSDate();
}

export function getLastWorkdayOfWeek(date: Date) {
  return DateTime.fromJSDate(date).endOf('week').minus({ days: 2 }).toJSDate();
}

export function getFollowingWorkday(date: Date) {
  const weekday = DateTime.fromJSDate(date).weekday;
  //TODO: check for 6-day week setting
  if (weekday < 6) {
    return date;
  } else {
    return DateTime.fromJSDate(date)
      .plus({ days: 8 - weekday })
      .toJSDate();
  }
}

export function getLastWorkday(date: Date) {
  const weekday = DateTime.fromJSDate(date).weekday;
  if (weekday < 6) {
    return date;
  } else {
    return DateTime.fromJSDate(date)
      .minus({ days: weekday - 5 })
      .toJSDate();
  }
}

export function getNextDay(date: Date) {
  return DateTime.fromJSDate(date).plus({ days: 1 }).toJSDate();
}

export function getPreviousDay(date: Date) {
  return DateTime.fromJSDate(date).minus({ days: 1 }).toJSDate();
}

export function getNextWorkday(date: Date) {
  return getFollowingWorkday(getNextDay(date));
}

export function getPreviousWorkday(date: Date) {
  return getLastWorkday(getPreviousDay(date));
}

export function getNextWeek(date: Date) {
  return DateTime.fromJSDate(date).plus({ days: 7 }).toJSDate();
}

export function getPreviousWeek(date: Date) {
  return DateTime.fromJSDate(date).minus({ days: 7 }).toJSDate();
}

export function getToday() {
  return new Date();
}

export function isWeekend(date: Date) {
  const weekday = DateTime.fromJSDate(date).weekday;
  return weekday === 6 || weekday === 7;
}

export function formatMonth(date: Date, hideYear: boolean) {
  return DateTime.fromJSDate(date).toLocaleString({
    month: 'long',
    year: hideYear ? undefined : 'numeric',
  });
}

export function isFirstDayOfMonth(date: Date) {
  if (new Date(date).getDate() === 1) {
    return true;
  } else if (
    [2, 3].includes(new Date(date).getDate()) &&
    new Date(date).getDay() === 1
  ) {
    // monday and it's the second or third day of the month
    return true;
  }
  return false;
}

export function formatDay(date: Date, shortDay = false, shortMonth = true) {
  return DateTime.fromJSDate(date).toLocaleString({
    weekday: shortDay ? 'short' : 'long',
    month: shortMonth ? 'numeric' : 'long',
    day: 'numeric',
  });
}

export function formatWeekDay(date: Date, short = false) {
  return DateTime.fromJSDate(date).toLocaleString({
    weekday: short ? 'short' : 'long',
  });
}

export function formatWorkWeek(date: Date, short = false) {
  const firstDay = getFirstDayOfWeek(date);
  const lastDay = getLastWorkdayOfWeek(date);
  // determine if first day and last day are in the same month
  if (firstDay.getMonth() === lastDay.getMonth()) {
    return (
      DateTime.fromJSDate(firstDay).toLocaleString({ day: 'numeric' }) +
      '. - ' +
      DateTime.fromJSDate(lastDay).toLocaleString({
        month: short ? 'short' : 'long',
        day: 'numeric',
      })
    );
  }
  return (
    DateTime.fromJSDate(firstDay).toLocaleString({
      month: short ? 'short' : 'long',
      day: 'numeric',
    }) +
    ' - ' +
    DateTime.fromJSDate(lastDay).toLocaleString({
      month: short ? 'short' : 'long',
      day: 'numeric',
    })
  );
}

export function formatCalendarWeek(date: Date, prefix: string) {
  return prefix + ' ' + getCalendarWeek(date);
}

export function formatDateMachine(date: Date): string {
  return DateTime.fromJSDate(date).toFormat('yyyy-MM-dd');
}

export function formatMachineToDate(dateString: string) {
  return DateTime.fromString(dateString, 'yyyy-MM-dd').toJSDate();
}

export function formatDate(dateString: string, format: string) {
  return DateTime.fromString(dateString, 'yyyy-MM-dd').toFormat(format);
}

export function formatTime(date: Date) {
  return DateTime.fromJSDate(date).toFormat('HH:mm');
}

export function calculateDifference(date1: Date, date2: Date): number {
  return Math.round(
    DateTime.fromJSDate(date1)
      .diff(DateTime.fromJSDate(date2), ['days'])
      .toObject().days,
  );
}

export function calculateDurationInMinutes(start: string, end: string) {
  if (!start || !end) {
    return NaN;
  }
  const startTimeInMinutes: number = start
    .split(':')
    .reduce((acc, time) => 60 * acc + Number.parseInt(time), 0);

  const endTimeInMinutes: number = end
    .split(':')
    .reduce((acc, time) => 60 * acc + Number.parseInt(time), 0);

  return endTimeInMinutes - startTimeInMinutes;
}

export function getWorkdayForUrl(urlDate?: string | null) {
  if (urlDate) {
    return new Date(urlDate);
  }
  if (isWeekend(new Date())) {
    return getFollowingWorkday(new Date());
  }
  return new Date();
}

export function isInPast(date: Date) {
  return (
    DateTime.fromJSDate(date).startOf('day') <
    DateTime.fromJSDate(getToday()).startOf('day')
  );
}

export function isInPastDateTime(
  dateString: string,
  timeString: string,
): boolean {
  const date = formatMachineToDate(dateString);
  if (isInPast(date)) {
    return true;
  } else if (isToday(date)) {
    const time = new Date();
    const timeStrings = timeString.split(':');
    time.setHours(timeStrings[0], timeStrings[1], 0);

    const now = new Date();
    return time < now;
  }
  return false;
}

export function isSameDay(first: Date, second: Date): boolean {
  return DateTime.fromJSDate(first).hasSame(DateTime.fromJSDate(second), 'day');
}

export function isToday(date: Date) {
  return DateTime.fromJSDate(date).hasSame(DateTime.local(), 'day');
}

export function getDateRangeForAgendaView(
  dateString?: string,
  numberOfDays = 28,
) {
  const startDate = dateString ? formatMachineToDate(dateString) : new Date();
  const endDate = DateTime.fromJSDate(startDate)
    .plus({ days: numberOfDays })
    .toJSDate();

  const days = Array.from(Array(numberOfDays).keys()).map((weekDay: number) =>
    formatDateMachine(
      DateTime.fromJSDate(startDate).plus({ days: weekDay }).toJSDate(),
    ),
  );
  return {
    startDate: formatDateMachine(startDate),
    endDate: formatDateMachine(endDate),
    days: days,
    numberOfDays: numberOfDays,
  };
}

export function getDateRangeForWeekView(dateString?: string) {
  let date = dateString ? formatMachineToDate(dateString) : new Date();
  // if weekend, get next day
  if (isWeekend(date)) {
    date = getFollowingWorkday(date);
  }

  const { getPreference } = useCurrentUser();
  const preference = getPreference(UserPreferenceKeys.TIMETABLE_WEEKDAYS);

  const startDate = getFirstDayOfWeek(date);
  let endDate = getLastWorkdayOfWeek(date);
  if (preference === TimetableWeekdaysValues.SEVEN_DAYS) {
    endDate = getLastDayOfWeek(date);
  }

  //const days = Array.from(Array(5).keys()).map((weekDay: number) =>
  const days = Array.from(
    Array(preference === TimetableWeekdaysValues.SEVEN_DAYS ? 7 : 5).keys(),
  ).map((weekDay: number) =>
    formatDateMachine(
      DateTime.fromJSDate(startDate).plus({ days: weekDay }).toJSDate(),
    ),
  );

  return {
    startDate: formatDateMachine(startDate),
    endDate: formatDateMachine(endDate),
    days,
    numberOfDays: days.length,
  };
}

export function getDateRangeForYearView() {
  const date = new Date();
  const startDate = DateTime.fromJSDate(date).minus({ days: 365 }).toJSDate();
  const endDate = DateTime.fromJSDate(date).plus({ days: 365 }).toJSDate();

  return {
    startDate: formatDateMachine(startDate),
    endDate: formatDateMachine(endDate),
  };
}

export function getUTCDateFromTimestamp(machineDate?: string) {
  if (!machineDate) {
    console.error(
      'getUTCDateFromTimestamp: machineDate prop is undefined. Returning DateTime.now().toJSDate();.',
    );
    return DateTime.now().toJSDate();
  }
  const parsedDay = DateTime.fromISO(machineDate);
  // parse to UTC, 12AM to prevent day mismatch
  return DateTime.utc(
    parsedDay.year,
    parsedDay.month,
    parsedDay.day,
    12,
  ).toJSDate();
}

export function getUTCDateFromJSDate(date?: Date) {
  if (!date) {
    console.error(
      'getUTCDateFromJSDate: date prop is undefined. Returning DateTime.now().toJSDate();.',
    );
    return DateTime.now().toJSDate();
  }
  const parsedDay = DateTime.fromJSDate(date);
  // parse to UTC, 12AM to prevent day mismatch
  return DateTime.utc(
    parsedDay.year,
    parsedDay.month,
    parsedDay.day,
    12,
  ).toJSDate();
}

export function machineDateToTimetableWeekDay(
  machineDate: string,
): TimetableWeekDay {
  return {
    machineDate,
    date: formatMachineToDate(machineDate),
  };
}

export function dateToTimetableWeekDay(date: Date): TimetableWeekDay {
  return {
    machineDate: formatDateMachine(date),
    date,
  };
}

export function convertTimeToString(timeString: string) {
  if (timeString.indexOf(':') === -1 && timeString.length === 3) {
    timeString = `${timeString.slice(0, 1)}:${timeString.slice(1)}`;
  } else if (timeString.indexOf(':') === -1 && timeString.length === 4) {
    timeString = `${timeString.slice(0, 2)}:${timeString.slice(2)}`;
  }
  if (timeString.length === 4) {
    timeString = `0${timeString}`;
  }
  return timeString;
}

export function increaseTimeString(timeString: string, minutes: number) {
  let int = parseInt(timeString.replace(':', ''));
  const increment = Math.floor(minutes / 60) * 100 + (minutes % 60);
  int += increment;
  return convertTimeToString(int.toString());
}

export function isTodayInDateRange(start: string, end: string, today: string) {
  const startDate = new Date(formatDateMachine(new Date(start)));
  const endDate = new Date(formatDateMachine(new Date(end)));
  const todayDate = new Date(formatDateMachine(new Date(today)));

  return startDate <= todayDate && endDate >= todayDate;
}

export default defineNuxtPlugin(() => {
  const sessionStore = useSession();

  Settings.defaultLocale = sessionStore.getUserLanguage.toLowerCase();

  return {
    provide: {
      getCalendarWeek,
      getFirstDayOfWeek,
      getLastDayOfWeek,
      getLastWorkdayOfWeek,
      getFollowingWorkday,
      getLastWorkday,
      getNextDay,
      getPreviousDay,
      getNextWorkday,
      getPreviousWorkday,
      getNextWeek,
      getPreviousWeek,
      getToday,
      formatDay,
      formatDayShort,
      formatWorkWeek,
      formatWeekDay,
      formatCalendarWeek,
      formatDateMachine,
      formatMachineToDate,
      formatDate,
      formatMonth,
      getFirstAndLastWorkDayWeekFromWeekAndYearNumber,
      getCalendarYearNumber,
      isWeekend,
      calculateDifference,
      calculateDurationInMinutes,
      getWorkdayForUrl,
      isInPast,
      isInPastDateTime,
      isSameDay,
      isToday,
      isFirstDayOfMonth,
      getDateRangeForWeekView,
      getDateRangeForAgendaView,
      formatTime,
      getUTCDateFromJSDate,
      getUTCDateFromTimestamp,
      machineDateToTimetableWeekDay,
      dateToTimetableWeekDay,
      formatDayLong,
      convertTimeToString,
      increaseTimeString,
      formatDayLongWithYear,
      formatDayShortWithYear,
      getDateRangeForYearView,
    },
  };
});
